// Outlook Calendar OAuth integration utility
// Handles the complete OAuth flow for Outlook/Microsoft 365 calendar access
// Uses Microsoft Graph API v1.0

/**
 * Microsoft Graph API OAuth2 flow voor Outlook Calendar
 * 
 * Setup vereisten:
 * 1. Azure Portal: App registratie maken
 * 2. Redirect URI: {origin}/outlook-callback toevoegen
 * 3. API Permissions: Calendars.ReadWrite, User.Read
 * 4. Client Secret genereren
 * 5. Environment variables instellen (zie .env.example)
 */

export async function connectOutlookCalendar(): Promise<void> {
  console.log('🔵 Outlook Calendar Connect - Starting OAuth flow');
  
  // Get client ID from environment
  const clientId = process.env.REACT_APP_OUTLOOK_CLIENT_ID || process.env.NEXT_PUBLIC_OUTLOOK_CLIENT_ID;
  
  if (!clientId) {
    throw new Error('Outlook Client ID not configured');
  }

  // Build redirect URI (must match Azure App registration)
  // Use environment variable or fallback to production/development defaults
  const redirectUri = process.env.REACT_APP_OUTLOOK_REDIRECT_URI || 
    (process.env.NODE_ENV === 'production' 
      ? 'https://www.superkonnected.com/outlook-callback'
      : 'http://localhost:3001/outlook-callback');
  
  // Store return URL for after OAuth completes
  sessionStorage.setItem('outlookCalendarReturnUrl', window.location.hash || '#agenda');
  
  // Microsoft OAuth2 authorization URL
  const authUrl = new URL('https://login.microsoftonline.com/common/oauth2/v2.0/authorize');
  authUrl.searchParams.set('client_id', clientId);
  authUrl.searchParams.set('response_type', 'code');
  authUrl.searchParams.set('redirect_uri', redirectUri);
  authUrl.searchParams.set('response_mode', 'query');
  authUrl.searchParams.set('scope', [
    'openid',
    'email',
    'profile',
    'offline_access',
    'Calendars.ReadWrite',
    'User.Read'
  ].join(' '));
  authUrl.searchParams.set('state', generateStateToken());
  
  // Redirect to Microsoft login
  console.log('🔑 Redirecting to Microsoft OAuth:', authUrl.toString());
  window.location.href = authUrl.toString();
}

/**
 * Handle OAuth callback from Microsoft
 * Called on /outlook-callback page
 */
export async function handleOutlookCallback(): Promise<{ success: boolean; error?: string }> {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');
  const error = params.get('error');
  const errorDescription = params.get('error_description');
  
  if (error) {
    console.error('❌ Outlook OAuth error:', error, errorDescription);
    return {
      success: false,
      error: errorDescription || error
    };
  }
  
  if (!code) {
    return {
      success: false,
      error: 'No authorization code received'
    };
  }
  
  try {
    console.log('🔑 Outlook auth code received, calling Firebase Function...');
    
    // Call Firebase Cloud Function to exchange code for tokens
    const { getFunctions, httpsCallable } = await import('firebase/functions');
    const functions = getFunctions(undefined, 'europe-west1');
    const linkOutlookAccount = httpsCallable(functions, 'linkOutlookAccount');
    
    // Use same redirect URI logic as connectOutlookCalendar
    const redirectUri = process.env.REACT_APP_OUTLOOK_REDIRECT_URI || 
      (process.env.NODE_ENV === 'production' 
        ? 'https://www.superkonnected.com/outlook-callback'
        : 'http://localhost:3001/outlook-callback');
    
    const result = await linkOutlookAccount({
      code: code,
      redirectUri: redirectUri
    });
    
    const data = result.data as { connected?: boolean; success?: boolean; message?: string; error?: string };
    
    if (data.connected || data.success) {
      console.log('✅ Outlook Calendar connection successful');
      
      // Trigger success event for UI update
      window.dispatchEvent(new CustomEvent('outlookCalendarConnected', { detail: data }));
      
      return { success: true };
    } else {
      throw new Error(data.error || data.message || 'Failed to connect Outlook Calendar');
    }
  } catch (error: any) {
    console.error('❌ Error in Outlook Calendar connection:', error);
    
    // Trigger error event for UI update
    window.dispatchEvent(new CustomEvent('outlookCalendarError', {
      detail: error.message || 'Network error while connecting Outlook Calendar'
    }));
    
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Sync Outlook Calendar events
 */
export async function syncOutlookCalendar(): Promise<{ success: boolean; eventCount?: number; error?: string }> {
  try {
    const { getFunctions, httpsCallable } = await import('firebase/functions');
    const functions = getFunctions(undefined, 'europe-west1');
    const syncOutlook = httpsCallable(functions, 'syncOutlookCalendar');
    
    console.log('🔄 Syncing Outlook Calendar...');
    const result = await syncOutlook({});
    
    const data = result.data as { success?: boolean; eventCount?: number; message?: string; error?: string };
    
    if (data.success) {
      console.log('✅ Outlook Calendar sync successful:', data.eventCount, 'events');
      return {
        success: true,
        eventCount: data.eventCount || 0
      };
    } else {
      throw new Error(data.error || data.message || 'Failed to sync Outlook Calendar');
    }
  } catch (error: any) {
    console.error('❌ Error syncing Outlook Calendar:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Disconnect Outlook Calendar
 */
export async function disconnectOutlookCalendar(): Promise<{ success: boolean; error?: string }> {
  try {
    const { getFunctions, httpsCallable } = await import('firebase/functions');
    const functions = getFunctions(undefined, 'europe-west1');
    const disconnectOutlook = httpsCallable(functions, 'disconnectOutlookCalendar');
    
    console.log('🔌 Disconnecting Outlook Calendar...');
    const result = await disconnectOutlook({});
    
    const data = result.data as { success?: boolean; message?: string; error?: string };
    
    if (data.success) {
      console.log('✅ Outlook Calendar disconnected');
      return { success: true };
    } else {
      throw new Error(data.error || data.message || 'Failed to disconnect Outlook Calendar');
    }
  } catch (error: any) {
    console.error('❌ Error disconnecting Outlook Calendar:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Update user's Outlook integration status in Firestore
 */
export async function updateOutlookIntegrationStatus(userId: string, connected: boolean): Promise<void> {
  const { doc, updateDoc } = await import('firebase/firestore');
  const { db } = await import('../firebase');
  
  await updateDoc(doc(db, 'users', userId), {
    'integrations.outlookCalendar': connected,
    'integrations.outlookCalendarConnectedAt': connected ? new Date() : null
  });
}

/**
 * Generate a secure state token for OAuth
 */
function generateStateToken(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
}
